import { ROUTE_BUILDER } from '@/constants/pages';
import useKeywordAutoSuggestManager from '@/features/searchV2/components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest.hooks';
import { KeywordSelection } from '@/features/searchV2/components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest.type';
import useLocationAutoSuggestManager from '@/features/searchV2/components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.hooks';
import useDateTimePickerManager from '@/features/searchV2/components/DateTimePicker/DateTimePicker.hooks';
import { BaseSearchManagerOutput } from '@/hooks/search/useSearch.types';
import { useHistory, useLocation } from 'react-router-dom';
import { buildDealKeywordSuggestionTarget } from './useDealsSearch.helpers';
import { isRedirectedFromPreview, useSmtOnlyToggle } from './useSmtOnlyToggle';

type UseDealsSearchFormProps = {
  searchManager: BaseSearchManagerOutput;
  loadLocationFromHistory: boolean;
};

const useDealsSearchForm = ({ searchManager, loadLocationFromHistory }: UseDealsSearchFormProps) => {
  const routerLocation = useLocation();
  const { isSmtOnlySearch, setIsSmtOnlySearch } = useSmtOnlyToggle(
    searchManager.type,
    isRedirectedFromPreview(routerLocation.state),
  );

  const history = useHistory();

  const keywordAutoSuggestManager = useKeywordAutoSuggestManager({
    initialState: {
      keyword: searchManager.q,
      suggestions: [],
      storedSuggestions: [],
      newServices: [],
      hasHistory: false,
      showSuggestions: true,
    },
    searchType: 'deals',
    onSelectKeywordSuggestion: handleOnSelectKeywordSuggestion,
  });

  const locationAutoSuggestManager = useLocationAutoSuggestManager({
    initialState: {
      locationValue: searchManager.location,
      hasHistory: false,
      suggestions: [],
      storedSuggestions: [],
      showSuggestions: true,
      isFetchingUserPosition: false,
    },
    loadFromHistory: loadLocationFromHistory,
    onSelectLocationSuggestion: handleOnSelectLocationSuggestion,
  });

  const dateTimePickerManager = useDateTimePickerManager({
    initialState: {
      startDate: undefined,
      endDate: undefined,
      timeOfDay: undefined,
      showCalendar: false,
    },
    onUpdateDateTime: () => {},
    onClearDateTime: () => {},
  });

  function handleOnSelectKeywordSuggestion(suggestion: KeywordSelection) {
    if (suggestion.type === 'deal') {
      const target = buildDealKeywordSuggestionTarget(suggestion, locationAutoSuggestManager.location || undefined);
      history.push(target, { searchContext: 'search_autocomplete' });
      return;
    }
    if (suggestion.type === 'brand_deal') {
      const target = ROUTE_BUILDER.brandDealsSerp(suggestion.slug, locationAutoSuggestManager.location || undefined);
      history.push(target, { searchContext: 'search_autocomplete' });
      return;
    }

    if (locationAutoSuggestManager.location) {
      keywordAutoSuggestManager.saveKeywordToHistory(suggestion.keyword);
      searchManager.performSearch(
        { q: suggestion.keyword, location: locationAutoSuggestManager.location },
        { searchContext: 'search_autocomplete', fromSearchForm: true },
      );
    }
  }

  function handleOnSelectLocationSuggestion({ searchContext, ...params }: any) {
    searchManager.performSearch(
      { ...params, q: keywordAutoSuggestManager.keyword },
      { searchContext, fromSearchForm: true },
    );
  }

  return {
    keywordAutoSuggestManager,
    locationAutoSuggestManager,
    dateTimePickerManager,
    isSmtOnlySearch,
    setIsSmtOnlySearch,
  };
};

export default useDealsSearchForm;
